import { Button, Typography } from "@platform/design-system";
import { Stack } from "@mui/material";

import type { DraftSummary } from "../../draftSummary";

export interface DraftRowProps {
  draft: DraftSummary;
  /** Opens the conversation this answers. */
  onOpenThread: (threadId: string) => void;
  /**
   * Throws the draft away. Absent on a surface that cannot write, which hides
   * the control rather than showing one that is refused.
   */
  onDiscard?: (draft: DraftSummary) => void;
  /** True while a discard is in flight, so it cannot double-fire. */
  isDiscarding?: boolean;
}

/**
 * One unsent reply.
 *
 * The design puts Approve & Send on a RUMI draft and Resume on the analyst's
 * own. Neither is here: sending is unimplemented until the secure-document
 * path lands (CD-2989), and resuming needs the composer that same work
 * carries. Opening the thread and throwing the draft away are what this row
 * can honestly offer.
 */
export const DraftRow = ({
  draft,
  onOpenThread,
  onDiscard,
  isDiscarding = false,
}: DraftRowProps): React.JSX.Element => (
  <Stack
    data-testid="draft-row"
    data-draft-id={draft.id}
    direction="row"
    alignItems="flex-start"
    spacing={2}
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
    }}
  >
    <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={0.5}>
      <Typography
        data-testid="draft-row-drafted"
        variant="caption"
        color="textSecondary"
      >
        {draft.draftedAt ? `Drafted ${draft.draftedAt}` : "Drafted"}
      </Typography>

      <Typography data-testid="draft-row-subject" variant="header-small">
        {draft.subject}
      </Typography>

      <Typography
        data-testid="draft-row-preview"
        variant="body-medium"
        color="textSecondary"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {draft.preview}
      </Typography>

      {draft.to && (
        <Typography
          data-testid="draft-row-to"
          variant="caption"
          color="textSecondary"
        >
          {`To: ${draft.to}`}
        </Typography>
      )}
    </Stack>

    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
      {onDiscard && (
        <Button
          data-testid="draft-row-discard"
          // Quieter than opening it. Throwing work away should be the less
          // inviting of the two things this row offers.
          variant="tertiary"
          size="sm"
          disabled={isDiscarding}
          onClick={() => onDiscard(draft)}
        >
          Discard
        </Button>
      )}

      <Button
        data-testid="draft-row-open"
        variant="secondary"
        size="sm"
        onClick={() => onOpenThread(draft.threadId)}
      >
        Open thread
      </Button>
    </Stack>
  </Stack>
);
